import { createTheme } from '@mui/material';
import {
  MTW_PRIMARY,
  MTW_ACCENT,
  LEVDATA_PRIMARY,
  LEVDATA_ACCENT
} from './brandPalettes';

export const theme = createTheme({
  palette: {
    primary: { ...MTW_PRIMARY },
    secondary: {
      main: '#F27405',
      100: '#037F8C',
      80: '#04ADBF',
      60: '#85C3CA'
    },
    accentPurple: { ...MTW_ACCENT },
    optional: {
      100: '#BF5B04',
      80: '#F27405',
      60: '#F4C9A1',
      40: '#31193D',
      20: '#988d9e',
      10: '#f2b305'
    },
    action: {
      grey: '#EDEDED',
      middleGrey: '#B0B0B0',
      greytext: '#878787',
      deepGrey: '#7B7B7B'
    },
    divisory: {
      colorBorder: '#DED8E3',
      mediumBorder: '#d8d8d8',
      contrastBorder: '#C7C7C7',
      harshBorder: '#AAAAAA'
    },
    background: {
      main: '#F6F7FB',
      standard: '#FFFFFF',
      outmost: '#F5F3F7',
      table: 'rgba(243, 244, 246, 0.87)',
      100: '#FCFCFC'
    },
    text: {
      primary: '#000',
      secondary: '#A9A9A9',
      voidTitle: '#5D5E66',
      voidText: '#B9B9B9',
      boxedTitle: '#423F46',
      floatingTitle: '#000'
    },
    textlight: {
      main: '#ffffff'
    }
  },
  typography: {
    fontFamily: [
      'Figtree',
      'Nunito',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif'
    ].join(','),
    // Escala de tamanho/peso por variante do MUI (h1-h6, body1/2, caption,
    // overline) - a mesma fonte já era usada em quase toda tela, mas cada
    // lugar escolhia um `fontSize` em px na mão. Isso dá um vocabulário
    // único pra usar via <Typography variant="..."> ou
    // `sx={{ typography: '...' }}` em vez de números soltos.
    // Propositalmente NÃO mexe em `button` (mantém o textTransform padrão
    // do MUI) - várias telas já dependem do Button customizado do app
    // (components/MTWActions/Button), redefinir a variante nativa aqui
    // mudaria a aparência de botões que ninguém pediu pra mudar.
    h1: { fontSize: 32, fontWeight: 700, lineHeight: 1.25 },
    h2: { fontSize: 24, fontWeight: 700, lineHeight: 1.3 },
    h3: { fontSize: 20, fontWeight: 700, lineHeight: 1.3 },
    h4: { fontSize: 18, fontWeight: 600, lineHeight: 1.35 },
    h5: { fontSize: 16, fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: 14, fontWeight: 600, lineHeight: 1.4 },
    body1: { fontSize: 14, fontWeight: 400, lineHeight: 1.45 },
    body2: { fontSize: 13, fontWeight: 400, lineHeight: 1.45 },
    caption: { fontSize: 12, fontWeight: 400, lineHeight: 1.4 },
    overline: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '.08em',
      lineHeight: 1.4
    }
  }
});

// Tokens de tipografia fora do namespace `typography` do MUI - pensados
// pra qualquer `fontSize`/`fontWeight` escrito na mão (inline sx/style),
// que é a maioria do código hoje (poucos lugares usam <Typography
// variant="...">). Espelham os mesmos valores de src/styles/tokens.css
// (--font-size-*) - mude os dois em conjunto.
export const fontSize = {
  xs: '11px',
  sm: '12px',
  base: '13px',
  md: '14px',
  lg: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px'
};

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};

// Troca a paleta "primary"/"accentPurple" do tema COMPARTILHADO (mutação no
// mesmo objeto, não substituição da referência) entre o roxo da MTW e o
// Navy/Coral da LevData, conforme a rota atual - chamado de App.js a cada
// navegação (ver `isLevdataRoute` lá). Mutação porque várias telas mais
// antigas importam `theme` direto (fora de Context) e leem
// `theme.palette.primary...` a cada render - só substituir a referência
// passada ao MuiThemeProvider não alcançaria essas telas. Devolve um clone
// raso (referência nova) pra quem consome via Context do MUI (ex.:
// `color="primary"` nativo, `useTheme()`) perceber a troca também.
export const applyBrandPalette = (isLevdata) => {
  Object.assign(
    theme.palette.primary,
    isLevdata ? LEVDATA_PRIMARY : MTW_PRIMARY
  );
  Object.assign(
    theme.palette.accentPurple,
    isLevdata ? LEVDATA_ACCENT : MTW_ACCENT
  );
  return { ...theme, palette: { ...theme.palette } };
};

export default theme;
